import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import backofficeApiClient, {
  TimesheetStatusEnum,
  AssignmentTypeFilterEnum,
  TimesheetReportResponseModel,
  SummaryCustomerResponseModel,
  SummaryAssignmentResponseModel,
  SummaryAssignmentEndingSoonResponseModel,
  SendTimesheetStatusReportDetailRequestModel,
  FinancialReportsRequestModel,
  SortingEnum,
  AgingSummarySortFieldEnum,
  AgingDetailDateRangeTypeEnum,
  TrendBillingSortFieldEnum,
  BillingAndProfitSortFieldEnum,
  AverageRatesSortFieldEnum,
  FinancialReportFieldFilterModel,
  FinancialReportSortFieldEnum,
  FinancialFilterViewModel,
} from 'api/backofficeApiClient';
import adminApiClient from 'api/adminApiClient';
import { logout } from './authenticationSlice';
import { RootState } from './rootReducer';

interface HomeDashboardState {
  preffered: any;
  summary: {
    activeCustomers: {
      data: SummaryCustomerResponseModel | null;
      loading: boolean;
    };
    activeAssignments: {
      data: SummaryAssignmentResponseModel | null;
      loading: boolean;
    };
    timesheetStatusReport: {
      data: TimesheetReportResponseModel | null;
      loading: boolean;
    };
    assignmentsEndingSoon: {
      data: SummaryAssignmentEndingSoonResponseModel | null;
      loading: boolean;
    };
  };
  reportsDetailsCount: {
    trendWeeklyBillingProfitCount: number | null;
    customerBillingProfitCount: number | null;
    customerTrendReportCount: number | null;
    averageRatesReportCount: number | null;
    customerMixProfitReportCount: number | null;
    trendWeeklyBillingAndProfitReportCount: number | null;
    agingSummaryReportCount: number | null;
  };
}

const initialState: HomeDashboardState = {
  preffered: null,
  summary: {
    activeCustomers: {
      data: null,
      loading: false,
    },
    activeAssignments: {
      data: null,
      loading: false,
    },
    timesheetStatusReport: {
      data: null,
      loading: false,
    },
    assignmentsEndingSoon: {
      data: null,
      loading: false,
    },
  },
  reportsDetailsCount: {
    trendWeeklyBillingProfitCount: null,
    customerBillingProfitCount: null,
    customerTrendReportCount: null,
    averageRatesReportCount: null,
    customerMixProfitReportCount: null,
    trendWeeklyBillingAndProfitReportCount: null,
    agingSummaryReportCount: null,
  },
};

interface DateRangeRequestModel {
  startDate?: Date;
  endDate?: Date;
}

const getMyCustomers = createAsyncThunk(
  'homeDashboard/getMyCustomers',
  async () => {
    return backofficeApiClient.getMyCustomers();
  },
);

const getMyCustomersDetail = createAsyncThunk(
  'homeDashboard/getMyCustomersDetail',
  async (page: number) => {
    return backofficeApiClient.getMyCustomersDetail(page);
  },
);

const getReportsDatesRangeInfo = createAsyncThunk(
  'homeDashboard/getReportsDatesRangeInfo',
  async () => {
    return backofficeApiClient.getReportsDatesRangeInfo();
  },
);

const setNewEndDateForReports = createAsyncThunk(
  'homeDashboard/setNewEndDateForReports',
  async (newDate: Date | undefined) => {
    return backofficeApiClient.setNewEndDateForReports(newDate);
  },
);

const getMyActiveAssignments = createAsyncThunk(
  'homeDashboard/getMyActiveAssignments',
  async () => {
    return backofficeApiClient.getMyActiveAssignments();
  },
  {
    condition: (_, { getState }) => {
      const { homeDashboard } = getState() as RootState;

      return !homeDashboard.summary.activeAssignments.loading;
    },
  },
);

export interface GetAssignmentsDetailModel {
  page?: number;
  assignmentTypes?: AssignmentTypeFilterEnum[];
}

const getMyActiveAssignmentsDetail = createAsyncThunk(
  'homeDashboard/getMyActiveAssignmentsDetail',
  async ({ page, assignmentTypes }: GetAssignmentsDetailModel) => {
    return backofficeApiClient.getMyActiveAssignmentsDetail(
      page,
      assignmentTypes,
    );
  },
);

const getMyActiveAssignmentsDetailExcel = createAsyncThunk(
  'homeDashboard/getMyActiveAssignmentsDetailExcel',
  async (assignmentTypes: AssignmentTypeFilterEnum[]) => {
    const result =
      await backofficeApiClient.getMyActiveAssignmentsDetailExcel(
        assignmentTypes,
      );
    const url = URL.createObjectURL(result.data);
    return { url, fileName: result.fileName };
  },
);

const getAssignmentsEndingSoonDetailExcel = createAsyncThunk(
  'homeDashboard/getAssignmentsEndingSoonDetailExcel',
  async (assignmentTypes: AssignmentTypeFilterEnum[]) => {
    const result =
      await backofficeApiClient.getAssignmentsEndingSoonDetailExcel(
        assignmentTypes,
      );
    const url = URL.createObjectURL(result.data);
    return { url, fileName: result.fileName };
  },
);

export interface TimesheetStatusReportExcelRequestModel
  extends DateRangeRequestModel {
  pageNumber: number;
  pageSize: number;
  states?: TimesheetStatusEnum[];
  customerIds?: string[];
  workerIds?: string[];
  companyIds?: string[];
  isAdmin?: boolean;
}

const getTimesheetStatusReportDetailExcel = createAsyncThunk(
  'homeDashboard/getTimesheetStatusReportDetailExcel',
  async ({
    pageNumber,
    pageSize,
    states,
    customerIds,
    workerIds,
    companyIds,
    startDate,
    endDate,
    isAdmin,
  }: TimesheetStatusReportExcelRequestModel) => {
    const result = isAdmin
      ? await adminApiClient.getTimesheetStatusReportDetailsExcel(
          pageNumber,
          pageSize,
          states,
          customerIds,
          workerIds,
          startDate,
          endDate,
          companyIds,
        )
      : await backofficeApiClient.getTimesheetStatusReportDetailsExcel(
          pageNumber,
          pageSize,
          states,
          customerIds,
          workerIds,
          startDate,
          endDate,
          undefined,
        );

    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

const getMyCustomersDetailExcel = createAsyncThunk(
  'homeDashboard/getMyCustomersDetailExcel',
  async () => {
    const result = await backofficeApiClient.getMyActiveCustomersDetailExcel();
    const url = URL.createObjectURL(result.data);
    return { url, fileName: result.fileName };
  },
);

const getTimesheetStatusReport = createAsyncThunk(
  'homeDashboard/getTimesheetStatusReport',
  async () => {
    return backofficeApiClient.getTimesheetStatusReport(5);
  },
);

const getAssignmentsEndingSoon = createAsyncThunk(
  'homeDashboard/getAssignmentsEndingSoon',
  async () => {
    return backofficeApiClient.getAssignmentsEndingSoon();
  },
);

const getAssignmentsEndingSoonDetail = createAsyncThunk(
  'homeDashboard/getAssignmentsEndingSoonDetail',
  async ({ page, assignmentTypes }: GetAssignmentsDetailModel) => {
    return backofficeApiClient.getAssignmentsEndingSoonDetail(
      page,
      assignmentTypes,
    );
  },
);

export interface TimesheetStatusReportDetailsRequestModel
  extends DateRangeRequestModel {
  pageNumber?: number;
  pageSize?: number;
  state: TimesheetStatusEnum[];
  customerIds?: string[];
  workerIds?: string[];
  companyIds?: string[];
  isAdmin?: boolean;
}

const getTimesheetStatusReportDetails = createAsyncThunk(
  'homeDashboard/getTimesheetStatusReportDetails',
  async ({
    pageNumber,
    pageSize,
    state,
    customerIds,
    workerIds,
    companyIds,
    startDate,
    endDate,
    isAdmin,
  }: TimesheetStatusReportDetailsRequestModel) => {
    return isAdmin
      ? adminApiClient.getTimesheetStatusReportNotSubmittedDetails(
          pageNumber,
          pageSize,
          state,
          customerIds,
          workerIds,
          startDate,
          endDate,
          companyIds,
        )
      : backofficeApiClient.getTimesheetStatusReportNotSubmittedDetails(
          pageNumber,
          pageSize,
          state,
          customerIds,
          workerIds,
          startDate,
          endDate,
          undefined,
        );
  },
);

const getWorkersForTimesheetStatusReport = createAsyncThunk(
  'homeDashboard/getWorkersForTimesheetStatusReport',
  async (isAdmin: boolean | undefined) => {
    return isAdmin
      ? adminApiClient.getWorkersForTimesheetStatusReport()
      : backofficeApiClient.getWorkersForTimesheetStatusReport();
  },
);

const getCustomersForTimesheetStatusReport = createAsyncThunk(
  'homeDashboard/getCustomersForTimesheetStatusReport',
  async (isAdmin: boolean | undefined) => {
    return isAdmin
      ? adminApiClient.getCustomersForTimesheetStatusReport()
      : backofficeApiClient.getCustomersForTimesheetStatusReport();
  },
);

const getMembersForAdminTimesheetStatusReport = createAsyncThunk(
  'homeDashboard/getMembersForAdminTimesheetStatusReport',
  async () => {
    return adminApiClient.getMembersForTimesheetStatusReport();
  },
);

interface SendTimesheetStatusReportRequestModel
  extends SendTimesheetStatusReportDetailRequestModel {
  isAdmin?: boolean;
}
const getValuesForFinancialReportFilters = createAsyncThunk(
  'homeDashboard/getValuesForFinancialReportFilters',
  async () => {
    return backofficeApiClient.getAllFinancialReportsFilters();
  },
);

const sendTimesheetStatusReportDetailsExcel = createAsyncThunk(
  'homeDashboard/sendTimesheetStatusReportDetailsExcel',
  async ({ isAdmin, ...data }: SendTimesheetStatusReportRequestModel) => {
    return isAdmin
      ? adminApiClient.sendTimesheetStatusReportDetailsExcel(data)
      : backofficeApiClient.sendTimesheetStatusReportDetailsExcel(data);
  },
);

export interface WeeklyReportsRequestModel extends DateRangeRequestModel {
  pageNumber?: number;
  sortType?: SortingEnum;
  sortField?: TrendBillingSortFieldEnum;
  pageSize?: number;
}

const getWeeklyReportData = createAsyncThunk(
  'homeDashboard/getWeeklyReportData',
  async (data: WeeklyReportsRequestModel) => {
    return backofficeApiClient.getWeeklyTrendBillingReport(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
  },
);

interface BillingProfitReportData extends DateRangeRequestModel {
  pageNumber?: number;
  sortType?: SortingEnum;
  sortField?: BillingAndProfitSortFieldEnum;
  pageSize?: number;
}

const getBillingProfitReportData = createAsyncThunk(
  'homeDashboard/getBillingProfitReportData',
  async (data: BillingProfitReportData) => {
    return backofficeApiClient.getBillingAndProfitByCustomerReport(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
  },
);

const getBillingProfitReportExcel = createAsyncThunk(
  'homeDashboard/getBillingProfitReportExcel',
  async (data: BillingProfitReportData) => {
    const result =
      await backofficeApiClient.getBillingAndProfitByCustomerReportExcel(
        data.startDate,
        data.endDate,
        data.pageSize,
        data.pageNumber,
        data.sortField,
        data.sortType,
      );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

interface AverageRatesReportData extends DateRangeRequestModel {
  pageNumber?: number;
  pageSize?: number;
  sortField?: AverageRatesSortFieldEnum;
  sortType?: SortingEnum;
}

const getAverageRatesReportData = createAsyncThunk(
  'homeDashboard/getAverageRatesReportData',
  async (data: AverageRatesReportData) => {
    return backofficeApiClient.getAverageRatesByCustomerReport(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
  },
);

const getAverageRatesReportExcel = createAsyncThunk(
  'homeDashboard/getAverageRatesReportExcel',
  async (data: AverageRatesReportData) => {
    const result = await backofficeApiClient.getAverageRatesByCustomerExcel(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

const getWeeklyReportExcel = createAsyncThunk(
  'homeDashboard/getWeeklyReportExcel',
  async (data: WeeklyReportsRequestModel) => {
    const result = await backofficeApiClient.getWeeklyTrendReportExcel(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

const getTrendWeeklyBilligProfitChartData = createAsyncThunk(
  'homeDashboard/getTrendWeeklyBilligProfitChartData',
  async (data: WeeklyReportsRequestModel) => {
    return backofficeApiClient.getSummaryWeeklyTrendBillingReport(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
  },
);

interface AgingReportRequestModel {
  customerKey: string;
  pageNumber: number;
  sortType?: SortingEnum;
  dateRangeType: AgingDetailDateRangeTypeEnum;
  pageSize: number;
}

const getAgingReportDetails = createAsyncThunk(
  'homeDashboard/getAgingReportDetails',
  async (data: AgingReportRequestModel) => {
    return backofficeApiClient.getAgingDetailReport(
      data.customerKey,
      data.pageNumber,
      data.sortType,
      data.pageSize,
      data.dateRangeType,
    );
  },
);

const getAgingReportDetailsExcel = createAsyncThunk(
  'homeDashboard/getAgingReportDetailsExcel',
  async (data: AgingReportRequestModel) => {
    const result = await backofficeApiClient.getAgingDetailExcelReport(
      data.customerKey,
      data.pageNumber,
      data.sortType,
      data.pageSize,
      data.dateRangeType,
    );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

const getFinancialReportDetails = createAsyncThunk(
  'homeDashboard/getFinancialReportDetails',
  async (data: FinancialReportsRequestModel) => {
    return backofficeApiClient.getFinancialReportsPagedList(data);
  },
);

const getFinancialReportsSettings = createAsyncThunk(
  'homeDashboard/getFinancialReportsSettings',
  async () => {
    return backofficeApiClient.getFinancialReportsSettings();
  },
);

const configureFinancialReportColumns = createAsyncThunk(
  'homeDashboard/configureFinancialReportColumns',
  async (data: FinancialFilterViewModel) => {
    return backofficeApiClient.setFinancialReportsSettings(data);
  },
);

export interface AgingSummaryReportsRequestModel {
  pageNumber?: number;
  sortType?: SortingEnum;
  sortField?: AgingSummarySortFieldEnum;
  pageSize?: number;
}

const getAgingSummaryReport = createAsyncThunk(
  'homeDashboard/getAgingSummaryReport',
  async (data: AgingSummaryReportsRequestModel) => {
    return backofficeApiClient.getAgingSummaryReport(
      data.pageNumber,
      data.sortType,
      data.sortField,
      data.pageSize,
    );
  },
);

interface getAgingDetailTotalReportModel {
  pageNumber?: number;
  sortType?: SortingEnum;
  dateRangeType?: AgingDetailDateRangeTypeEnum;
  pageSize?: number;
}

const getAgingDetailTotalReport = createAsyncThunk(
  'homeDashboard/getAgingDetailTotalReport',
  async (data: getAgingDetailTotalReportModel) => {
    return backofficeApiClient.getAgingDetailTotalReport(
      data.pageNumber,
      data.sortType,
      data.pageSize,
      data.dateRangeType,
    );
  },
);

export interface AgingDetailTotalReportRequestModel {
  pageNumber?: number;
  sortType?: SortingEnum;
  pageSize?: number;
  dateRangeType?: AgingDetailDateRangeTypeEnum;
}

const getAgingDetailTotalReportExcel = createAsyncThunk(
  'homeDashboard/getAgingDetailTotalReportExcel',
  async (data: AgingDetailTotalReportRequestModel) => {
    const result = await backofficeApiClient.getAgingTotalDetailExcelReport(
      data.pageNumber,
      data.sortType,
      data.pageSize,
      data.dateRangeType,
    );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

export interface AgingTotalBalanceReportRequestModel {
  pageNumber?: number;
  sortType?: SortingEnum;
  pageSize?: number;
  customerKey: string;
}

const getAgingReportTotalBalance = createAsyncThunk(
  'homeDashboard/getAgingReportTotalBalance',
  async (data: AgingTotalBalanceReportRequestModel) => {
    return backofficeApiClient.getAgingDetailReport(
      data.customerKey,
      data.pageNumber,
      data.sortType,
      data.pageSize,
      AgingDetailDateRangeTypeEnum.MoreThen0,
    );
  },
);

const getAgingReportTotalBalanceExcel = createAsyncThunk(
  'homeDashboard/getAgingReportTotalBalanceExcel',
  async (data: AgingTotalBalanceReportRequestModel) => {
    const result = await backofficeApiClient.getAgingDetailExcelReport(
      data.customerKey,
      data.pageNumber,
      data.sortType,
      data.pageSize,
      AgingDetailDateRangeTypeEnum.MoreThen0,
    );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

const getAgingSummaryReportExcel = createAsyncThunk(
  'homeDashboard/getAgingSummaryReportExcel',
  async (data: AgingSummaryReportsRequestModel) => {
    const result = await backofficeApiClient.getAgingSummaryExcelReport(
      data.pageNumber,
      data.sortType,
      data.sortField,
      data.pageSize,
    );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

const getFinancialReportExcel = createAsyncThunk(
  'homeDashboard/getFinancialReportExcel',
  async (data: FinancialReportsRequestModel) => {
    const result = await backofficeApiClient.getFinancialReportsExcel(data);
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

interface CustomerTrendChartRequestModel extends DateRangeRequestModel {
  pageNumber?: number;
  sortType?: SortingEnum;
  sortField?: string;
  pageSize?: number;
}
const getCustomerTrendChartData = createAsyncThunk(
  'homeDashboard/getCustomerTrendChartData',
  async (data: CustomerTrendChartRequestModel) => {
    return backofficeApiClient.getSummaryCustomerMixTrendReport(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
  },
);

interface WeeklyReportByCustomerRequestModel extends DateRangeRequestModel {
  pageSize?: number;
  pageNumber?: number;
  sortField?: string;
  sortType?: SortingEnum;
}

const getWeeklyTrendMixReport = createAsyncThunk(
  'homeDashboard/getWeeklyTrendMixReport',
  async (data: WeeklyReportByCustomerRequestModel) => {
    return backofficeApiClient.getWeeklyTrendMixReport(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
  },
);

const getCustomerTrendWeeklyProfitReport = createAsyncThunk(
  'homeDashboard/getCustomerTrendWeeklyProfitReport',
  async (data: WeeklyReportByCustomerRequestModel) => {
    return backofficeApiClient.getWeeklyTrendProfitReport(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
  },
);

const getCustomerTrendWeeklyProfitReportExcel = createAsyncThunk(
  'homeDashboard/getCustomerTrendWeeklyProfitReportExcel',
  async (data: WeeklyReportByCustomerRequestModel) => {
    const result = await backofficeApiClient.getWeeklyTrendProfitReportExcel(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

const getWeeklyTrendMixReportExcel = createAsyncThunk(
  'homeDashboard/getWeeklyTrendMixReportExcel',
  async (data: WeeklyReportByCustomerRequestModel) => {
    const result = await backofficeApiClient.getTrendMixBillingReportExcel(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
    const url = URL.createObjectURL(result.data);

    return { url, fileName: result.fileName };
  },
);

interface CustomerBillingProfitRequestModel extends DateRangeRequestModel {
  pageSize?: number;
  pageNumber?: number;
  sortType?: SortingEnum;
  sortField?: BillingAndProfitSortFieldEnum;
}
const getCustomerBillingProfitChartData = createAsyncThunk(
  'homeDashboard/getCustomerBillingProfitChartData',
  async (data: CustomerBillingProfitRequestModel) => {
    return backofficeApiClient.getBillingAndProfitByCustomerReportAll(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
  },
);

interface PreviewAgingReportRequestModel {
  pageSize?: number;
  pageNumber?: number;
  sortType?: SortingEnum;
  sortField?: AgingSummarySortFieldEnum;
}
const getPreviewAgingSummaryReport = createAsyncThunk(
  'homeDashboard/getPreviewAgingSummaryReport',
  async (data: PreviewAgingReportRequestModel) => {
    return backofficeApiClient.getPreviewAgingSummaryReport(
      data.pageNumber,
      data.sortType,
      data.sortField,
      data.pageSize,
    );
  },
);

interface PreviewTrendWeeklyBillingAndProfitReportRequestModel
  extends DateRangeRequestModel {
  pageSize?: number;
  pageNumber?: number;
  sortType?: SortingEnum;
  sortField?: FinancialReportSortFieldEnum;
  fieldsFilter?: FinancialReportFieldFilterModel[];
}
const getPreviewTrendWeeklyBillingAndProfitReport = createAsyncThunk(
  'homeDashboard/getPreviewTrendWeeklyBillingAndProfitReport',
  async (data: PreviewTrendWeeklyBillingAndProfitReportRequestModel) => {
    return backofficeApiClient.getPreviewTrendWeeklyBillingAndProfitReport(
      data.pageNumber,
      data.sortField,
      data.sortType,
      data.startDate,
      data.endDate,
      data.fieldsFilter,
    );
  },
);

interface CustomerMixProfitChartRequestModel extends DateRangeRequestModel {
  pageNumber?: number;
  sortType?: SortingEnum;
  sortField?: string;
  pageSize?: number;
}
const getCustomerMixProfitChartData = createAsyncThunk(
  'homeDashboard/getCustomerMixProfitChartData',
  async (data: CustomerMixProfitChartRequestModel) => {
    return backofficeApiClient.getSummaryCustomerProfitTrendReport(
      data.startDate,
      data.endDate,
      data.pageSize,
      data.pageNumber,
      data.sortField,
      data.sortType,
    );
  },
);

const homeDashboardSlice = createSlice({
  name: 'homeDashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      Object.assign(state, initialState);
    });
    builder.addCase(getMyCustomers.pending, (state) => {
      state.summary.activeCustomers.loading = true;
    });
    builder.addCase(getMyCustomers.fulfilled, (state, action) => {
      state.summary.activeCustomers.loading = false;
      state.summary.activeCustomers.data = action.payload;
    });
    builder.addCase(getMyCustomers.rejected, (state) => {
      state.summary.activeCustomers.loading = false;
    });
    builder.addCase(getMyActiveAssignments.pending, (state) => {
      state.summary.activeAssignments.loading = true;
    });
    builder.addCase(getMyActiveAssignments.fulfilled, (state, action) => {
      state.summary.activeAssignments.loading = false;
      state.summary.activeAssignments.data = action.payload;
    });
    builder.addCase(getMyActiveAssignments.rejected, (state) => {
      state.summary.activeAssignments.loading = false;
    });
    builder.addCase(getTimesheetStatusReport.pending, (state) => {
      state.summary.timesheetStatusReport.loading = true;
    });
    builder.addCase(getTimesheetStatusReport.fulfilled, (state, action) => {
      state.summary.timesheetStatusReport.loading = false;
      state.summary.timesheetStatusReport.data = action.payload;
    });
    builder.addCase(getTimesheetStatusReport.rejected, (state) => {
      state.summary.timesheetStatusReport.loading = false;
    });
    builder.addCase(getAssignmentsEndingSoon.pending, (state) => {
      state.summary.assignmentsEndingSoon.loading = true;
    });
    builder.addCase(getAssignmentsEndingSoon.fulfilled, (state, action) => {
      state.summary.assignmentsEndingSoon.loading = false;
      state.summary.assignmentsEndingSoon.data = action.payload;
    });
    builder.addCase(getAssignmentsEndingSoon.rejected, (state) => {
      state.summary.assignmentsEndingSoon.loading = false;
    });
    builder.addCase(
      getTrendWeeklyBilligProfitChartData.fulfilled,
      (state, action) => {
        state.reportsDetailsCount.trendWeeklyBillingProfitCount =
          action.payload.length;
      },
    );
    builder.addCase(getTrendWeeklyBilligProfitChartData.rejected, (state) => {
      state.reportsDetailsCount.trendWeeklyBillingProfitCount = null;
    });
    builder.addCase(
      getCustomerBillingProfitChartData.fulfilled,
      (state, action) => {
        state.reportsDetailsCount.customerBillingProfitCount =
          action.payload.length;
      },
    );
    builder.addCase(getCustomerBillingProfitChartData.rejected, (state) => {
      state.reportsDetailsCount.customerBillingProfitCount = null;
    });
    builder.addCase(getCustomerTrendChartData.fulfilled, (state, action) => {
      state.reportsDetailsCount.customerTrendReportCount =
        action.payload.length;
    });
    builder.addCase(getCustomerTrendChartData.rejected, (state) => {
      state.reportsDetailsCount.customerTrendReportCount = null;
    });
    builder.addCase(getAverageRatesReportData.fulfilled, (state, action) => {
      state.reportsDetailsCount.averageRatesReportCount =
        action.payload.totalCount || null;
    });
    builder.addCase(getAverageRatesReportData.rejected, (state) => {
      state.reportsDetailsCount.averageRatesReportCount = null;
    });
    builder.addCase(
      getCustomerMixProfitChartData.fulfilled,
      (state, action) => {
        state.reportsDetailsCount.customerMixProfitReportCount =
          action.payload.length || null;
      },
    );
    builder.addCase(getCustomerMixProfitChartData.rejected, (state) => {
      state.reportsDetailsCount.customerMixProfitReportCount = null;
    });
    builder.addCase(
      getPreviewTrendWeeklyBillingAndProfitReport.fulfilled,
      (state, action) => {
        state.reportsDetailsCount.trendWeeklyBillingAndProfitReportCount =
          action.payload.length || null;
      },
    );
    builder.addCase(
      getPreviewTrendWeeklyBillingAndProfitReport.rejected,
      (state) => {
        state.reportsDetailsCount.trendWeeklyBillingAndProfitReportCount = null;
      },
    );
    builder.addCase(getPreviewAgingSummaryReport.fulfilled, (state, action) => {
      state.reportsDetailsCount.agingSummaryReportCount =
        action.payload.length || null;
    });
    builder.addCase(getPreviewAgingSummaryReport.rejected, (state) => {
      state.reportsDetailsCount.agingSummaryReportCount = null;
    });
  },
});

const { reducer } = homeDashboardSlice;

export {
  getMyCustomers,
  getMyCustomersDetail,
  getFinancialReportDetails,
  getMyCustomersDetailExcel,
  getMyActiveAssignments,
  getFinancialReportExcel,
  getMyActiveAssignmentsDetail,
  getMyActiveAssignmentsDetailExcel,
  getTimesheetStatusReport,
  getTimesheetStatusReportDetails,
  getWorkersForTimesheetStatusReport,
  getAssignmentsEndingSoon,
  getCustomersForTimesheetStatusReport,
  getAssignmentsEndingSoonDetail,
  getAssignmentsEndingSoonDetailExcel,
  sendTimesheetStatusReportDetailsExcel,
  getTimesheetStatusReportDetailExcel,
  getValuesForFinancialReportFilters,
  getMembersForAdminTimesheetStatusReport,
  getAgingReportDetailsExcel,
  getAgingSummaryReport,
  getAgingReportDetails,
  getWeeklyReportData,
  getWeeklyReportExcel,
  getAgingSummaryReportExcel,
  getCustomerTrendChartData,
  getTrendWeeklyBilligProfitChartData,
  getWeeklyTrendMixReportExcel,
  getWeeklyTrendMixReport,
  getCustomerBillingProfitChartData,
  getBillingProfitReportData,
  getAverageRatesReportData,
  getBillingProfitReportExcel,
  getAverageRatesReportExcel,
  getPreviewAgingSummaryReport,
  getCustomerMixProfitChartData,
  getPreviewTrendWeeklyBillingAndProfitReport,
  getCustomerTrendWeeklyProfitReport,
  getCustomerTrendWeeklyProfitReportExcel,
  configureFinancialReportColumns,
  getFinancialReportsSettings,
  getAgingReportTotalBalance,
  getAgingReportTotalBalanceExcel,
  getAgingDetailTotalReport,
  getAgingDetailTotalReportExcel,
  setNewEndDateForReports,
  getReportsDatesRangeInfo,
};
export default reducer;
